import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  AccountGradingRecording,
  TranscriptSearchMatch,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  QuoteMatcher,
  QuoteMatchMap,
  type QuoteRecordingMatch,
} from "../gradeValidationHelpers.js";

/** A prev/next navigation stop, one per surfaced snippet. */
export interface SearchNavTarget {
  snippetIndex: number;
  quoteId: string;
  recordingId: RecordingId;
}

/**
 * Turn a grading's search snippets into transcript highlights + an ordered list
 * of navigation targets, reusing the citation render path (one
 * `QuoteRecordingMatch` = one fragment-anchored span to paint; a located chunk
 * is one such span per fragment it crosses).
 *
 * Per snippet, its full chunk is located in the transcript (via `QuoteMatcher`),
 * then whichever applies:
 * - the server found query words in the chunk (`chunk_highlights` non-empty):
 *   highlight the WORDS, mapped onto the chunk's spans.
 * - none found (semantic-only match): highlight the whole chunk.
 *
 * The quote id per snippet is `search-<index>`, so its first span is
 * `quote-search-<index>-0` — the anchor `focusQuote` scrolls/flashes.
 */
export function buildSearchSnippetMatches(params: {
  recordings: readonly AccountGradingRecording[];
  snippets: readonly TranscriptSearchMatch[];
}): { searchMatches: QuoteMatchMap; navTargets: SearchNavTarget[] } {
  const { recordings, snippets } = params;

  const highlightSpans: QuoteRecordingMatch[] = [];
  const navTargets: SearchNavTarget[] = [];

  snippets.forEach((snippet, snippetIndex) => {
    const quoteId = `search-${snippetIndex}`;
    // Locate within the snippet's own recording so the chunk resolves within
    // the call it came from rather than the first global match.
    const snippetRecording = recordings.find(
      (r) => r.id === snippet.recording_id,
    );
    if (!snippetRecording) {
      return;
    }
    const chunkSpans = QuoteMatchMap.findByQuote(
      QuoteMatcher.new([snippetRecording]).findMatches([
        {
          type: "quote",
          id: quoteId,
          short: snippet.chunk_text,
          long: snippet.chunk_text,
        },
      ]),
      quoteId,
    );
    if (!chunkSpans || chunkSpans.length === 0) {
      // Chunk not locatable in the transcript — no navigation target.
      return;
    }

    const wordSpans = mapChunkHighlightsToWordSpans({
      recording: snippetRecording,
      chunkSpans,
      quoteId,
      chunkText: snippet.chunk_text,
      chunkHighlights: snippet.chunk_highlights,
    });
    // Fall back to full chunk highlighting
    const spansForSnippet = wordSpans.length > 0 ? wordSpans : [...chunkSpans];

    highlightSpans.push(...spansForSnippet);
    navTargets.push({
      snippetIndex,
      quoteId,
      recordingId: snippet.recording_id,
    });
  });

  return {
    searchMatches: QuoteMatchMap.fromMatches(highlightSpans),
    navTargets,
  };
}

/**
 * Map the server's chunk-relative highlight ranges onto fragment coordinates
 * by walking the chunk's spans (consecutive, one separator between fragments —
 * mirroring how the chunk text was assembled). A range whose mapped fragment
 * text doesn't reproduce the expected chunk substring (e.g. the chunk was
 * fuzzy-located, skewing offsets) is dropped; the caller then falls back to
 * highlighting the whole chunk.
 */
function mapChunkHighlightsToWordSpans(params: {
  recording: AccountGradingRecording;
  chunkSpans: readonly QuoteRecordingMatch[];
  quoteId: string;
  chunkText: string;
  chunkHighlights: TranscriptSearchMatch["chunk_highlights"];
}): QuoteRecordingMatch[] {
  const { recording, chunkSpans, quoteId, chunkText, chunkHighlights } = params;
  const wordSpans: QuoteRecordingMatch[] = [];
  let quoteIndex = 0;
  // Offset of the current chunk span within `chunkText` — each span covers
  // `chunkText[spanStart, spanStart + chunkSpan.length)`.
  let spanStart = 0;

  for (const chunkSpan of chunkSpans) {
    const fragment =
      recording.transcript?.fragments[chunkSpan.recordingFragmentIndex];
    for (const range of chunkHighlights) {
      // Translate the range from chunk-text space into this span's local
      // space; out-of-bounds means the range belongs to a different span.
      const startInSpan = range.start - spanStart;
      const endInSpan = range.end - spanStart;
      if (!fragment || startInSpan < 0 || endInSpan > chunkSpan.length) {
        continue;
      }
      // Span-local → fragment coordinates (the span starts mid-fragment when
      // the chunk begins partway through a turn).
      const inFragmentIndex = chunkSpan.inFragmentIndex + startInSpan;
      // Fuzzy-located chunks can skew all of the above; only keep the word if
      // the fragment really contains the highlighted chunk substring here.
      const expected = chunkText.slice(range.start, range.end);
      const actual = fragment.text_transcript.slice(
        inFragmentIndex,
        inFragmentIndex + expected.length,
      );
      if (actual.toLowerCase() !== expected.toLowerCase()) {
        continue;
      }
      wordSpans.push({
        quoteId,
        quoteIndex: quoteIndex++,
        recordingId: chunkSpan.recordingId,
        recordingFragmentIndex: chunkSpan.recordingFragmentIndex,
        inFragmentIndex,
        index: inFragmentIndex,
        length: expected.length,
      });
    }
    // Advance past this span plus the one-character separator between
    // fragments (chunk text joins fragments with a single space).
    spanStart += chunkSpan.length + 1;
  }

  return wordSpans;
}
